<template>
  <div class="sell-out-add">
    <NavHeaderBar :showSearch="false" :navBarTitle="$t('button.sellOut')">
      <div class="delete-btn" v-if="sellOutId" @click="handleDelete">
        {{ $t('button.delete') }}
      </div>
    </NavHeaderBar>

    <div class="banner-tip" @click="handleTips">
      <van-icon name="label-o" class="tip-icon" />
      <span class="desc-txt txt-ellipsis">{{ desc }}</span>
      <van-icon name="arrow" color="#333" />
    </div>

    <div class="content-wrapper">
      <div class="section-header" v-if="scheduleList.length > 0">
        <span class="label">{{ $t('shared.title.whichDay') }}:</span>
        <div class="product-name-header">
          <van-popover
            v-model="showPopover"
            trigger="click"
            placement="bottom-end"
          >
            <template #reference>
              <div class="title-wrapper">
                <div class="title-name txt-ellipsis">
                  {{ currentSchedule.reportDate }}
                  <van-icon name="play" class="arrow-icon" :class="{ 'expanded': showPopover }" />
                </div>
              </div>
            </template>
            <div class="popover-content">
              <div
                v-for="item in scheduleList"
                :key="item.schedule"
                class="popover-item"
                :class="{ 'active': currentSchedule.schedule === item.schedule, 'disabled': item.isLock }"
                @click.stop="handleReport(item)"
              >
                <div class="icon-wrapper">
                  <van-icon v-if="currentSchedule.schedule === item.schedule" name="success" />
                  <van-icon v-else-if="item.isLock" name="lock" />
                </div>
                <span class="text">{{ item.reportDate }}</span>
              </div>
            </div>
          </van-popover>
        </div>
      </div>

      <!-- Products Sold Section -->
      <div class="section-title">{{ $t('shared.title.theNumberOfProductsSold') }}:</div>
      <div class="product-list">
        <div class="list-header">
          <span>{{ $t('shared.filter.product') }}</span>
          <span>{{ $t('button.sellOut') }} ({{ productTotal }})</span>
        </div>
        <div v-for="(item, index) in productListLocal" :key="'prod_' + index" class="product-item">
          <img v-if="item.imgUrl" class="icon-img" :src="item.imgUrl" alt="">
          <img v-else class="icon-img" src="@/assets/images/icon_noimg.png" alt="">
          <div class="product-name txt-ellipsis">{{ item.productName }}</div>
          <div class="stepper-custom">
            <div class="step-btn" @click="updateCount(item, -5, 'product')">-5</div>
            <div class="step-btn" @click="updateCount(item, -1, 'product')">-</div>
            <van-field class="step-value" v-model="item.productCount" type="digit" :border="false" />
            <div class="step-btn" @click="updateCount(item, 1, 'product')">+</div>
            <div class="step-btn" @click="updateCount(item, 5, 'product')">+5</div>
          </div>
        </div>
      </div>

      <!-- PO Upload -->
      <div class="section-title">{{ $t('form.pO') }}:</div>
      <div class="upload-wrapper">
        <van-uploader v-model="poFileList" :after-read="(files, detail) => afterRead(files, detail, 'poFileList')" />
      </div>

      <!-- Sample Consumed Section -->
      <div class="section-title flex-between">
        <span>{{ $t('form.sampleConsumed') }}:</span>
      </div>
      <div class="product-list">
        <div class="list-header">
          <span>{{ $t('form.sample') }}</span>
          <span>{{ $t('form.sentOut') }} ({{ sampleTotal }})</span>
        </div>
        <div v-for="(item, index) in protoListLocal" :key="'sample_' + index" class="product-item">
          <img v-if="item.imgUrl" class="icon-img" :src="item.imgUrl" alt="">
          <img v-else class="icon-img" src="@/assets/images/icon_noimg.png" alt="">
          <div class="product-info">
            <div class="product-name txt-ellipsis">{{ item.productName }}</div>
            <div class="inventory">{{ $t('form.inventory') }}: {{ item.inventoryCount }}</div>
          </div>
          <div class="stepper-custom">
            <div class="step-btn" @click="updateCount(item, -5, 'sample')">-5</div>
            <div class="step-btn" @click="updateCount(item, -1, 'sample')">-</div>
            <van-field class="step-value" v-model="item.productCount" type="digit" :border="false" />
            <div class="step-btn" @click="updateCount(item, 1, 'sample')">+</div>
            <div class="step-btn" @click="updateCount(item, 5, 'sample')">+5</div>
          </div>
        </div>
      </div>

      <!-- Sample Photo Upload -->
      <div class="section-title">{{ $t('form.samplePhoto') }}:</div>
      <div class="upload-wrapper">
        <van-uploader v-model="sampleFileList" :after-read="(files, detail) => afterRead(files, detail, 'sampleFileList')" />
      </div>
    </div>

    <div class="footer-action">
      <van-button type="info" block @click="handleSave">{{ $t('button.save') }}</van-button>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import {
  arsEventSellOutAddOrUpdateApi,
  arsEventSellOutInfoApi,
  arsEventSellOutDeleteApi
} from '@/api/event'
import { Toast, Notify, Dialog } from 'vant'

export default {
  name: 'SellOutAdd',
  data () {
    return {
      eventId: '',
      sellOutId: '',
      productListLocal: [],
      protoListLocal: [],
      poFileList: [],
      sampleFileList: [],
      showPopover: false,
      scheduleList: [],
      currentSchedule: {},
      desc: '',
      newTips: ''
    }
  },
  computed: {
    ...mapState('event', ['eventInfo']),
    sampleTotal () {
      return this.protoListLocal.reduce(
        (acc, cur) => acc + (Number(cur.productCount) || 0),
        0
      )
    },
    productTotal () {
      return this.productListLocal.reduce(
        (acc, cur) => acc + (Number(cur.productCount) || 0),
        0
      )
    }
  },
  activated () {
    this.eventId = this.$route.query.eventId
    this.sellOutId = this.$route.query.sellOutId
    this.getScheduleList()
  },
  methods: {
    ...mapMutations('event', ['SET_EVENTINFO']),
    handleTips () {
      Dialog.alert({
        message: this.newTips,
        confirmButtonText: this.$t('button.gotIt'),
        confirmButtonColor: '#307bff',
        messageAlign: 'left'
      }).then(() => {
        // on close
      })
    },
    getScheduleList () {
      Toast.loading({
        message: this.$t('shared.loading'),
        forbidClick: true,
        duration: 0
      })
      arsEventSellOutInfoApi(this.eventId, { sellOutId: this.sellOutId, schedule: this.eventInfo.schedule }).then((res) => {
        Toast.clear()
        const { productList, protoList, poImgUrl, protoImgUrl, scheduleList, policyInfo } = res.data
        this.productListLocal = productList
        this.protoListLocal = protoList
        this.poFileList = poImgUrl ? poImgUrl.split(',').map(imgItem => {
          return { url: imgItem }
        }) : []
        this.sampleFileList = protoImgUrl ? protoImgUrl.split(',').map(imgItem => {
          return { url: imgItem }
        }) : []
        scheduleList.forEach((subItem, subIndex) => {
          subItem.reportDate = `${subItem.reportDate} · ${this.$t('shared.day')} ${subIndex + 1}`
          if (subItem.isChoose && this.eventInfo.schedule !== '-1') {
            this.currentSchedule = subItem
          }
        })
        this.scheduleList = scheduleList
        this.newTips = ''
        policyInfo.planList.forEach(planItem => {
          // 购买Product A/B 10 Pcs 送 B 2 Pcs
          const productName = planItem.productList.map(nameItem => nameItem.productName).join(' / ')
          const protoName = planItem.protoList.map(nameItem => nameItem.productName).join(' / ')
          this.newTips += `·${this.$t('shared.buy')} ${productName} ${planItem.buyCount} ${this.$t('form.pcs')} ${this.$t('shared.get')} ${protoName} ${planItem.giveCount} ${this.$t('form.pcs')} \n`
        })
        this.desc = policyInfo.policyName
      })
    },
    updateCount (item, delta, type) {
      let val = Number(item.productCount) || 0
      const originalVal = val

      // Calculate new value
      val += delta
      if (val < 0) val = 0

      // If it's sample (protoListLocal), we need to check and update inventory
      if (type === 'sample') {
        const currentInventory = Number(item.inventoryCount) || 0
        // Calculate the difference between new value and original value
        const difference = val - originalVal

        // Update inventory regardless of current value (allow negative)
        // If count increases (difference > 0), inventory decreases
        // If count decreases (difference < 0), inventory increases
        this.$set(item, 'inventoryCount', currentInventory - difference)
      }

      this.$set(item, 'productCount', val)
    },
    afterRead (files, detail, key) {
      Toast.loading({
        message: this.$t('shared.loading'),
        forbidClick: true,
        duration: 0
      })
      this.$uploadImg(files)
        .then((res) => {
          Toast.clear()
          this.$set(this[key], detail.index, {
            url: res.url
          })
        })
        .catch((err) => {
          console.log('err', err)
          Toast.clear()
          Toast(this.$t('tips.uploadFailed'))
        })
    },
    handleSave () {
      if (this.productTotal <= 0) {
        Toast(this.$t('tips.pleaseFillInTheSales'))
        return
      }
      if (this.eventInfo.schedule === '-1') {
        Toast(this.$t('tips.pleaseChooseWhichDayToSell'))
        return
      }
      const data = {
        id: this.sellOutId || '',
        schedule: this.eventInfo.schedule,
        eventId: this.eventId,
        productList: this.productListLocal,
        protoList: this.protoListLocal,
        poImgUrl: this.poFileList.map(imgItem => imgItem.url).join(','),
        protoImgUrl: this.sampleFileList.map(imgItem => imgItem.url).join(',')
      }

      Toast.loading({
        message: this.$t('shared.loading'),
        forbidClick: true,
        duration: 0
      })

      arsEventSellOutAddOrUpdateApi(data)
        .then(() => {
          Toast.clear()
          Notify({ type: 'success', message: this.$t('shared.success'), duration: 1500 })
          this.$router.go(-1)
        })
        .catch((err) => {
          console.error(err)
        })
    },
    handleReport (params) {
      if (params.isLock || this.currentSchedule.schedule === params.schedule) return
      this.currentSchedule = params
      this.SET_EVENTINFO({ eventId: this.eventId, schedule: params.schedule })
      this.getScheduleList()
      this.showPopover = false
    },
    handleDelete () {
      Dialog.confirm({
        message: this.$t('tips.messageFifteen'),
        confirmButtonText: this.$t('button.clear'),
        cancelButtonText: this.$t('button.cancel'),
        confirmButtonColor: '#d9001b'
      })
        .then(() => {
          Toast.loading({
            message: this.$t('shared.loading'),
            forbidClick: true,
            duration: 0
          })
          arsEventSellOutDeleteApi(this.sellOutId).then(res => {
            Toast.clear()
            this.$router.go(-1)
          })
        })
        .catch(() => {
          // on cancel
        })
    }
  }
}
</script>

<style lang="less" scoped>
.sell-out-add {
  padding-bottom: 80px;
  background-color: #fff;
  min-height: 100vh;

  .delete-btn {
    color: #ff4444;
  }

  .banner-tip {
    background-color: #f9d9ba;
    color: #ef5435;
    padding: 6px 15px;
    font-size: 12px;
    display: flex;
    align-items: center;
    .desc-txt {
      flex: 1;
    }
    .tip-icon {
      margin-right: 5px;
      font-size: 14px;
    }
  }

  .content-wrapper {
    padding: 15px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      .label {
        font-weight: bold;
        font-size: 16px;
        color: #333;
      }
      .day-selector {
        display: flex;
        align-items: center;
        color: #307bff;
        font-size: 14px;
        span {
          margin-right: 5px;
        }
      }
    }

    .product-name-header {
      color: #307bff;
      font-weight: bold;
      font-size: 14px;
      .title-name {
        display: flex;
        align-items: center;
        .arrow-icon {
          font-size: 12px;
          margin-left: 4px;
          transform: rotate(90deg);
          transition: transform 0.2s;
          &.expanded {
            transform: rotate(-90deg);
          }
        }
      }
    }

    .section-title {
      font-weight: bold;
      font-size: 14px;
      color: #333;
      padding-bottom: 10px;

      &.flex-between {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }

    .product-list {
      margin-bottom: 20px;
      .list-header {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        padding: 8px 2px;
        border-top: 2px solid #e9effc;
        border-bottom: 2px solid #e9effc;
        font-weight: bold;
      }

      .product-item {
        display: flex;
        align-items: center;
        padding: 8px 2px;
        margin-bottom: 15px;
        border-bottom: 1px solid #e9effc;
        &:last-child {
          border-bottom: 2px solid #e9effc;
        }
        .icon-img {
          width: 40px;
          height: 40px;
          margin-right: 10px;
          object-fit: contain;
        }
        .product-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 40px;
        }
        .product-name {
          width: 143px;
          flex: 1;
          font-size: 12px;
        }
        .inventory {
          font-size: 12px;
        }
        .stepper-custom {
          display: flex;
          align-items: center;
          border: 1px solid #b9cbed;
          border-radius: 4px;
          overflow: hidden;
          width: fit-content;
          margin-left: auto; // Align to right if needed, or keep standard

          .step-btn {
            width: 28px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fff;
            color: #323233;
            cursor: pointer;
            border-right: 1px solid #b9cbed;
            font-size: 12px;
            &:last-child {
              border-right: none;
            }
            &:active {
              background-color: #f2f3f5;
            }
          }
          .step-value {
            width: 32px;
            height: 24px;
            padding: 0;
            background-color: #fff;
            border-right: 1px solid #b9cbed;
            border-radius: 0;
            /deep/ .van-field__control {
              text-align: center;
              height: 24px;
              line-height: 24px;
              color: #323233;
              font-size: 12px;
            }
          }
        }
      }
    }

    .upload-wrapper {
      margin-bottom: 20px;
    }
  }

  .footer-action {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #fff;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  }
}
.popover-content {
  padding: 8px 0;
  min-width: 130px;
  .popover-item {
    display: flex;
    align-items: center;
    padding: 10px;
    font-size: 12px;
    color: #333;
    cursor: pointer;
    &.active {
      color: #307bff;
      background-color: #f5faff;
    }
    &.disabled {
      color: #ccc;
      cursor: not-allowed;
    }
    &:active:not(.disabled) {
      background-color: #f2f3f5;
    }
    .icon-wrapper {
      width: 20px;
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .text {
      flex: 1;
    }
  }
}
</style>
